import React, { useState, useEffect } from "react";
import { XMarkIcon, CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ImageWithFallback } from "../../../Utils/loader";
import { UrduInput } from "../../../Components/Shared/UrduInput";
import { ImageSeoFields } from "../../../Components/Shared/ImageSeoFields";

function AddCategoryModal({
  modalOpen,
  toggleModal,
  category,
  handleSaveCategory,
}) {
  const [categoryData, setCategoryData] = useState({
    name: "",
    categoryName_ur: "",
    image: null,
    image_alt: "",
    image_title: "",
    status: true,
  });

  const [nameError, setNameError] = useState("");
  const [imageError, setImageError] = useState("");
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (category) {
      setCategoryData({
        name: category.name,
        categoryName_ur: category.categoryName_ur || "",
        image: category.image,
        image_alt: category.image_alt || "",
        image_title: category.image_title || "",
        status: category.status,
      });
      if (category.image) {
        setImagePreview(category.image);
      }
    } else {
      setCategoryData({
        name: "",
        categoryName_ur: "",
        image: null,
        image_alt: "",
        image_title: "",
        status: true,
      });
      setImagePreview(null);
    }
  }, [category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryData((prevData) => ({
      ...prevData,
      [name]: value === "true" ? true : value === "false" ? false : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          // Temporarily commented out image resolution check (20x20)
          // if (img.width === 20 && img.height === 20) {
          //   setCategoryData((prevData) => ({
          //     ...prevData,
          //     image: file,
          //   }));
          //   setImagePreview(event.target.result);
          //   setImageError("");
          // } else {
          //   setCategoryData((prevData) => ({
          //     ...prevData,
          //     image: null,
          //   }));
          //   setImagePreview(null);
          //   setImageError("Image must be exactly 20x20 pixels.");
          // }
          setCategoryData((prevData) => ({
            ...prevData,
            image: file,
          }));
          setImagePreview(event.target.result);
          setImageError("");
        };
        img.onerror = () => {
          setCategoryData((prevData) => ({
            ...prevData,
            image: null,
          }));
          setImagePreview(null);
          setImageError("Unable to read image. Please upload a valid PNG/JPEG.");
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setCategoryData((prevData) => ({
      ...prevData,
      image: null,
    }));
    setImagePreview(null);
    setImageError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let formValid = true;

    setNameError("");
    setImageError((prev) => prev);

    if (!categoryData.name) {
      setNameError("Category name is required.");
      formValid = false;
    }

    if (!categoryData.image) {
      setImageError((prev) => prev || "Image is required.");
      formValid = false;
    }

    if (imageError) {
      formValid = false;
    }

    if (!formValid) {
      return;
    }

    handleSaveCategory(categoryData, category ? category.id : null);

    setCategoryData({
      name: "",
      categoryName_ur: "",
      image: null,
      image_alt: "",
      image_title: "",
      status: true,
    });
    setImagePreview(null);

    toggleModal();
  };

  const handleCloseModal = () => {
    setCategoryData({
      name: "",
      categoryName_ur: "",
      image: null,
      image_alt: "",
      image_title: "",
      status: true,
    });
    setImagePreview(null);
    setNameError("");
    setImageError("");
    toggleModal();
  };

  const isFormValid = categoryData.name.trim().length >= 2 && categoryData.image;

  return (
    modalOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-8 rounded-2xl w-[500px] max-h-[90vh] overflow-y-auto relative">
          <XMarkIcon
            className="absolute top-4 right-4 h-6 w-6 text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
            onClick={handleCloseModal}
          />
          <h3 className="text-2xl font-semibold mb-6 text-center text-gray-800">
            {category ? "Edit Category" : "Add Category"}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Upload Section */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Category Image <span className="text-red-500">*</span>
              </label>

              {imagePreview ? (
                <div className="relative">
                  <div className="w-full h-48 bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                    <ImageWithFallback
                      src={imagePreview}
                      alt="Category preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                    title="Remove image"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer w-full h-48 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  <CameraIcon className="h-12 w-12 text-gray-400 mb-3" />
                  <span className="text-gray-600 font-medium">Upload Image</span>
                  <span className="text-gray-400 text-sm mt-1">Click to browse files</span>
                </label>
              )}

              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />

              {imageError && (
                <p className="text-red-500 text-sm">{imageError}</p>
              )}
              <p className="text-gray-500 text-xs">
                Image is required for category creation
              </p>
              <ImageSeoFields
                imageAlt={categoryData.image_alt}
                imageTitle={categoryData.image_title}
                onAltChange={(value) =>
                  setCategoryData((prev) => ({ ...prev, image_alt: value }))
                }
                onTitleChange={(value) =>
                  setCategoryData((prev) => ({ ...prev, image_title: value }))
                }
              />
            </div>

            {/* Category Name Input */}
            <div className="space-y-2">
              <label htmlFor="category-name" className="block text-sm font-medium text-gray-700">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                id="category-name"
                type="text"
                name="name"
                value={categoryData.name}
                onChange={handleInputChange}
                placeholder="Enter category name"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                required
              />
              {nameError && (
                <p className="text-red-500 text-sm">{nameError}</p>
              )}
              <p className="text-gray-500 text-xs">
                Minimum 2 characters required
              </p>
              <UrduInput
                label="زمرہ کا نام"
                name="categoryName_ur"
                value={categoryData.categoryName_ur}
                onChange={handleInputChange}
              />
            </div>

            {/* Status Select */}
            <div className="space-y-2">
              <label htmlFor="category-status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="category-status"
                name="status"
                value={categoryData.status}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value={true}>Active</option>
                <option value={false}>Inactive</option>
              </select>
              <p className="text-gray-500 text-xs">
                Default status is Active
              </p>
            </div>

            <div className="flex justify-end items-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleCloseModal}
                className="btn btn-reset-filter"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid}
                className={`btn ${
                  category ? "btn-edit text-white" : "btn-primary"
                } ${!isFormValid ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {category ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );
}

export default AddCategoryModal;
